import {
  call,
  takeLatest,
  put,
  CallEffect,
  PutEffect,
  ForkEffect,
} from "redux-saga/effects";
import { actions } from "reducers/dashboard";
import { actions as slotCapacityActions } from "reducers/dashboardSlotCapacityModal";
import { actions as notificationActions } from "reducers/notification";
import * as dashboardMiddleware from "middleware/dashboard";
import {
  DelayedTripsResponseType,
  PointStatusResponseType,
  dashboardResponseDataType,
  dashboardResponseType,
} from "./types/dashboard";
import { AxiosResponse } from "axios";
import {
  DashboardFilterType,
  DashboardTransactionsDetail,
  Saturation,
  SlotCapacity,
} from "reducers/types/dashboard";
import { PayloadAction } from "@reduxjs/toolkit";
import { ApiResponseResult } from "middleware/api";
import {
  SlotDetail,
  SlotDetailReponse,
} from "reducers/types/dashboardSlotCapacityModal";
import { DASHBOARD_TABLES_LIMIT } from "utils/constants";
import { t } from "i18next";
import { store } from "store";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getDelayedTripsRequest.type, getDelayedTrips),
  takeLatest(actions.getDashboard.type, getDashboard),
  takeLatest(actions.getSaturationRequest.type, getSaturation),
  takeLatest(actions.getSlotCapacityRequest.type, getSlotCapacity),
  takeLatest(slotCapacityActions.getSlotDetailRequest.type, getSlotDetail),
  takeLatest(actions.getPointStatusRequest.type, getPointStatus),
  takeLatest(actions.getTransactionsDetailRequest.type, getTransactionsDetail),
];

export default sagas;

function* getDashboard({
  payload,
}: PayloadAction<DashboardFilterType>): Generator<
  | CallEffect<AxiosResponse<dashboardResponseDataType>>
  | PutEffect<{ type: string }>,
  void,
  dashboardResponseType
> {
  yield put(actions.getTransactionsDetailRequest(payload));
  yield put(
    actions.getDelayedTripsRequest({
      ...payload,
    })
  );
  yield put(
    actions.getPointStatusRequest({
      ...payload,
      offset: 0,
      limit: DASHBOARD_TABLES_LIMIT,
    })
  );

  yield put(actions.getSlotCapacityRequest(payload));
  yield put(actions.getSaturationRequest(payload));
}

function* getSaturation({
  payload,
}: PayloadAction<DashboardFilterType>): Generator<
  | CallEffect<AxiosResponse<ApiResponseResult<Saturation[]>>>
  | PutEffect<{ type: string }>,
  void,
  AxiosResponse<ApiResponseResult<Saturation[]>>
> {
  const response = yield call(dashboardMiddleware.getSaturation, payload);
  if (response.status !== 200) {
    yield put(actions.getSaturationError());
  } else {
    const { result } = response.data;
    yield put(actions.getSaturationSuccess(result));
  }
}

function* getSlotCapacity({
  payload,
}: PayloadAction<DashboardFilterType>): Generator<
  | CallEffect<AxiosResponse<ApiResponseResult<SlotCapacity[]>>>
  | PutEffect<{ type: string }>,
  void,
  AxiosResponse<ApiResponseResult<SlotCapacity[]>>
> {
  const response = yield call(dashboardMiddleware.getSlotCapacity, payload);
  if (response.status !== 200) {
    yield put(actions.getSlotCapacityError());
  } else {
    const { result } = response.data;
    yield put(actions.getSlotCapacitySuccess(result));
  }
}

function* getSlotDetail({
  payload,
}: PayloadAction<number>): Generator<
  | CallEffect<AxiosResponse<ApiResponseResult<SlotDetail>>>
  | PutEffect<{ type: string }>,
  void,
  AxiosResponse<SlotDetailReponse>
> {
  const response = yield call(dashboardMiddleware.getSlotDetail, payload);
  if (response.status !== 200) {
    if (
      response.data.statusCode === 50012 ||
      response.data.statusCode === 50013
    ) {
      const { dispatch, getState } = store;
      yield put(
        notificationActions.showNotification({
          level: "warning",
          title: t("dashboard:expiredSlotTitle"),
          body: t("dashboard:expiredSlotBody"),
          onConfirmLabel: t("global:update"),
          onCancelLabel: t("global:back"),
          onConfirm: () => {
            dispatch(
              slotCapacityActions.toggleCapacityDetailModalVisibility(false)
            );
            dispatch(actions.getDashboard(getState().dashboard.filters));
          },
        })
      );
    }
    yield put(slotCapacityActions.getSlotDetailError());
  } else {
    const { result } = response.data;
    yield put(slotCapacityActions.getSlotDetailSuccess(result));
  }
}
function* getDelayedTrips({
  payload,
}: PayloadAction<DashboardFilterType>): Generator<
  | CallEffect<AxiosResponse<DelayedTripsResponseType>>
  | PutEffect<{ type: string }>,
  void,
  AxiosResponse<DelayedTripsResponseType>
> {
  const response = yield call(dashboardMiddleware.getDelayedTrips, payload);
  if (response.status !== 200) {
    yield put(actions.getDelayedTripsError());
  } else {
    const { result, quantity } = response.data;
    yield put(
      actions.getDelayedTripsSuccess({ details: result.items, quantity })
    );
  }
}

function* getPointStatus({
  payload,
}: PayloadAction<DashboardFilterType>): Generator<
  | CallEffect<AxiosResponse<PointStatusResponseType>>
  | PutEffect<{ type: string }>,
  void,
  AxiosResponse<PointStatusResponseType>
> {
  const response = yield call(dashboardMiddleware.getPointStatus, payload);
  if (response.status !== 200) {
    yield put(actions.getPointStatusError());
  } else {
    const { result, quantity } = response.data;
    yield put(
      actions.getPointStatusSuccess({ details: result.items, quantity })
    );
  }
}

function* getTransactionsDetail({
  payload,
}: PayloadAction<DashboardFilterType>): Generator<
  | CallEffect<AxiosResponse<ApiResponseResult<DashboardTransactionsDetail>>>
  | PutEffect<{ type: string }>,
  void,
  AxiosResponse<ApiResponseResult<DashboardTransactionsDetail>>
> {
  const response = yield call(
    dashboardMiddleware.getTransactionsDetail,
    payload
  );
  if (response.status !== 200) {
    yield put(actions.getTransactionsDetailError());
  } else {
    const { result } = response.data;
    yield put(actions.getTransactionsDetailSuccess(result));
  }
}
